/**
 * PdfViewer.jsx
 * Mobile-taugliche PDF-Anzeige via pdf.js (Canvas-Rendering).
 * Grund: iOS Safari kann PDFs in <iframe> nicht skalieren/scrollen —
 * es zeigt riesig gezoomt nur einen Ausschnitt der ersten Seite.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, FileWarning } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export const PdfViewer = ({ url, className = '' }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let pdfDoc = null;
    setLoading(true);
    setError(null);

    const render = async () => {
      try {
        pdfDoc = await pdfjsLib.getDocument(url).promise;
        if (cancelled) return;
        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = '';
        const cssWidth = Math.max((container.clientWidth || window.innerWidth) - 16, 100);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        for (let i = 1; i <= pdfDoc.numPages; i++) {
          const page = await pdfDoc.getPage(i);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const scale = cssWidth / base.width;
          const viewport = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${cssWidth}px`;
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.style.margin = '0 auto 10px';
          canvas.style.background = '#fff';
          canvas.style.borderRadius = '6px';
          canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,0.25)';
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (i === 1 && !cancelled) setLoading(false);
        }
        if (!cancelled) setLoading(false);
      } catch (e) {
        console.error('PDF-Rendering fehlgeschlagen:', e);
        if (!cancelled) { setError('PDF konnte nicht angezeigt werden'); setLoading(false); }
      }
    };
    render();

    return () => {
      cancelled = true;
      if (pdfDoc) pdfDoc.destroy().catch(() => {});
    };
  }, [url]);

  return (
    <div className={`w-full ${className}`} data-testid="pdf-viewer">
      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
      {error && (
        <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
          <FileWarning className="h-6 w-6 opacity-50" />
          <p className="text-xs">{error}</p>
        </div>
      )}
      <div ref={containerRef} className="px-2 py-2" />
    </div>
  );
};
